import StyledTable, { Align } from "../../../StyledTable/StyledTable";
import { useLayoutEffect, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./HoldingPortfolio.module.scss";
import { getAvlBuyPoint, getPortfoliosPrivate, postAvlBuyPoint } from "@/controllers/gridManager/masterTransManager/masterTransManager";
import { Switch } from "@mui/material";
import { BoxProps, Stack, useTheme } from "@mui/system";
import CommonUtils from "@/utilities/utils";
import TypographyElement from "@/views/elements/typography/TypographyElement";
import { subSocketMarketManager } from "@/controllers/gridManager/socketManager/SocketManager";
import { useInstrumentsDynamic } from "@/controllers/gridManager/PriceboardManager";
import { useCosInfo } from "@/controllers/gridManager/accountsManager/accountManager";
import { useTranslation } from "react-i18next";
import { getInstrumentsBySymbolsNoSave } from "@/controllers/gridManager/datafeedManager/datafeedManager";
import PopupElement from "@/views/elements/popup/PopupElement";
import _ from "lodash";
import EventEmitter from 'eventemitter3';
import ViewNumberFormat from "@/views/elements/Inputs/ViewNumberFormat";
const ee = new EventEmitter()

const cx = classNames.bind(styles);

type HoldingPortfolioProps = {
  accountId?: string;
  masterCustodycd?: string;
  emitter?: any
}

interface RealtimeRendererProps extends BoxProps {
  symbol: string;
  defaultValue: any;
  CPcomputed: (cp: any) => any;
  render: (value: any) => ReactNode;
}

interface RealtimeCellRendererProps extends BoxProps {
  symbol: string;
  defaultValue: any;
  CPcomputed: (cp: any) => any;
  emitComputed: (symbol: any) => any;
  render: (value: any) => ReactNode;
}

type RendererProps = {
  value: number,
  item: any,
  computed?: (cp: any, item: any, subcp?: any, subitem?: any) => any
}

type RendererCellProps = {
  value: number,
  item: any,
  computed?: (cp: any, item: any, subcp?: any, subitem?: any) => any
  emitComputed: (symbol: any) => number;
}

const RealtimeRenderer = ({ defaultValue, symbol, CPcomputed, render }: RealtimeRendererProps): ReactNode => {
  const [value, setValue] = useState<any>(defaultValue);
  const { instrumentsDynamic } = useInstrumentsDynamic()

  useEffect(() => {
    let instrument: any = instrumentsDynamic?.find((i: any) => symbol === i.SB)
    if (instrument && instrument.CP && instrument.CP !== value) {
      setValue(CPcomputed(instrument.CP))
    }
  }, [instrumentsDynamic])

  return render(value)
}

const RealtimeCellRenderer = ({ defaultValue, symbol, CPcomputed, emitComputed, render }: RealtimeCellRendererProps): ReactNode => {
  const [value, setValue] = useState<any>(defaultValue);
  const { instrumentsDynamic } = useInstrumentsDynamic()

  useEffect(() => {
    let instrument: any = instrumentsDynamic?.find((i: any) => symbol === i.SB)
    if (instrument && instrument.CP && instrument.CP !== value) {
      setValue(CPcomputed(instrument.CP))
      ee.emit('cp', { symbol })
    }
  }, [instrumentsDynamic])

  useEffect(() => {
    ee.on('cp', (data: any) => {
      if (data.symbol !== symbol) setValue(emitComputed(symbol))
    });
  }, [])

  return render(value)
}

const MarketPriceRenderer = ({ value, item }: RendererProps) => {
  return <RealtimeRenderer symbol={item.symbol}
    defaultValue={value}
    CPcomputed={(cp: any) => cp}
    render={(value: any) => <TypographyElement><ViewNumberFormat align="end" displayType="text" value={CommonUtils.formatNumber(value)}></ViewNumberFormat></TypographyElement>}
  />
}

const DmRenderer = ({ value, item, computed = () => { }, emitComputed }: RendererCellProps) => {
  return <RealtimeCellRenderer symbol={item?.symbol}
    defaultValue={value}
    CPcomputed={(cp: any) => computed(cp, item)}
    emitComputed={(symbol: any) => emitComputed(symbol)}
    render={(value: any) => <TypographyElement><ViewNumberFormat align="end" displayType="text" value={CommonUtils.formatNumber(value)}></ViewNumberFormat>%</TypographyElement>}
  />
}

const PnlrateRenderer = ({ value, item, computed = () => { } }: RendererProps) => {
  return <RealtimeRenderer
    symbol={item.symbol}
    defaultValue={value}
    CPcomputed={(cp: any) => computed(cp, item)}
    render={(value: any) => <TypographyElement width='fit-content' ColorElement={value >= 0 ? 'up' : 'down'}>{item.symbol === "Tiền" ? '' : <><ViewNumberFormat align="end" displayType="text" color={value >= 0 ? 'up' : 'down'} value={CommonUtils.formatNumber(value)}></ViewNumberFormat>%</>}</TypographyElement>} />
}

const HoldingPortfolioRealtime = ({ accountId, masterCustodycd, emitter }: HoldingPortfolioProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [tableData, setTableData] = useState<any>([]);
  const [isRecommend, setIsRecommend] = useState(false);
  const [isOpenRecommendModal, setIsOpenRecommendModal] = useState(false);
  const { cosInfo } = useCosInfo()
  const dmRef = useRef([])
  const isMaster = !masterCustodycd && !accountId

  useLayoutEffect(() => {
    loadData(1, 9999)
    loadAvlBuyPoint()
  }, [])

  const loadData = async (pageIndex: number, pageSize: number) => {
    let response = await getPortfoliosPrivate(
      accountId || cosInfo.refaccount,
      masterCustodycd || '',
      pageIndex, pageSize)
    if (response.isSuccess) {
      let data = response.data
      let symbolList = data.map((d: { symbol: any; }) => d.symbol).filter((item: any) => item.symbol !== 'Tiền')
      if (symbolList.length) {
        let paramsSocket = {
          callerId: 'uniqueCallerId',
          idPbTab: 'defaultTab',
          symbols: symbolList
        }
        subSocketMarketManager(paramsSocket)
        let instrumentsResponse = await getInstrumentsBySymbolsNoSave({ symbols: symbolList })
        if (instrumentsResponse.isSuccess) {
          let instruments = instrumentsResponse.data
          const mergedArray = data.map((item1: any) => {
            let instrument = instruments.find((item2: any) => item2.symbol === item1.symbol)
            return {
              ...item1,
              ...instrument,
              marketprice: !!instrument && instrument.closePrice ? instrument.closePrice : item1.marketprice
            }
          });
          dmRef.current = _.cloneDeep(mergedArray)
          setTableData(mergedArray)
          emitter.emit('message', mergedArray)
        }
      } else {
        setTableData(data)
        emitter.emit('message', data)
      }
    }
  }

  const loadAvlBuyPoint = async () => {
    let response = await getAvlBuyPoint(accountId || cosInfo.refaccount)
    if (response.isSuccess) {
      setIsRecommend(response.data.avlbuypoint === 'Y' ? true : false)
    }
  }

  const sumMPComputed = (data: any[]) => data ? data.reduce((accumulator: any, portfolio: any) => {
    return accumulator + (portfolio.marketprice * portfolio.total_trade)
  }, 0) : 0;

  const dmComputed = (cp: number, item: any) => {
    let instrument = dmRef.current.find((el: any) => el.symbol === item.symbol) as any
    if (!!instrument) instrument.marketprice = cp
    return (cp * parseFloat(item.total_trade.split(',').join('')) / sumMPComputed(dmRef.current)) * 100
  }

  const dmEmitComputed = (symbol: any) => {
    if (symbol === 'Tiền') {
      let money = dmRef.current.find((el: any) => el.symbol === 'Tiền') as any
      return (money.total_trade / sumMPComputed(dmRef.current)) * 100
    } else {
      let instrument = dmRef.current.find((el: any) => el.symbol === symbol) as any
      return (instrument.marketprice * instrument.total_trade / sumMPComputed(dmRef.current)) * 100
    }
  }

  const pnlrateComputed = (cp: any, item: any) => {
    let costprice = parseFloat(item.costprice.split(',').join(''))
    return ((cp - costprice) / costprice) * 100
  }

  const cookedData = () => {
    let sumMP = sumMPComputed(tableData)
    return (
      tableData?.map(
        (portfolio: {
          symbol: string;
          total_trade: number;
          costprice: number;
          marketprice: number;
          sumMP: number;
        }) => {
          return portfolio.symbol === 'Tiền' ?
            {
              symbol: portfolio.symbol,
              costprice: '',
              total_trade: '',
              marketprice: portfolio.total_trade,
              dm: (portfolio.total_trade / sumMP) * 100,
              pnlrate: ''
            }
            : {
              symbol: portfolio.symbol,
              costprice: CommonUtils.formatNumber(portfolio.costprice),
              total_trade: CommonUtils.formatNumber(portfolio.total_trade),
              marketprice: portfolio.marketprice || 0,
              dm: (portfolio.marketprice * portfolio.total_trade / sumMP) * 100,
              pnlrate: (portfolio.marketprice - portfolio.costprice) / portfolio.costprice * 100,
            };
        }
      ) || []
    );
  };

  const handleToggleRecommendModal = () => {
    setIsOpenRecommendModal(true)
  }

  const handleConfirmRecommend = async () => {
    const response = await postAvlBuyPoint(cosInfo.refaccount, isRecommend ? 'N' : 'Y')
    if (response.isSuccess) {
      await loadAvlBuyPoint()
    }
    setIsOpenRecommendModal(false)
  }

  const tableConfig = {
    fields: [
      { valueName: "symbol", name: t('copytrade.common.symbol'), width: 90 },
      { valueName: "total_trade", name: t('copytrade.chart.holdingPortfolio.quantity'), width: 150, align: Align.right, render: (value: any) => <ViewNumberFormat align="end" value={value}></ViewNumberFormat> },
      { valueName: "costprice", name: t('copytrade.chart.holdingPortfolio.costprice'), width: 150, align: Align.right, render: (value: any) => <ViewNumberFormat align="end" value={value}></ViewNumberFormat> },
      { valueName: "marketprice", name: t('copytrade.chart.holdingPortfolio.marketPrice'), width: 110, align: Align.right, render: (value: any, item: any) => <MarketPriceRenderer value={value} item={item} /> },
      { valueName: "dm", name: t('copytrade.chart.holdingPortfolio.dm'), width: 160, align: Align.right, render: (value: any, item: any) => <DmRenderer value={value} item={item} computed={dmComputed} emitComputed={dmEmitComputed} /> },
      { valueName: "pnlrate", name: t('copytrade.chart.holdingPortfolio.pnlrate'), width: 160, align: Align.right, render: (value: any, item: any) => <PnlrateRenderer value={value} item={item} computed={pnlrateComputed} /> },
    ],
    pagination: {
      enabled: false,
    },
  };

  return (
    <>
      <Stack p='20px' spacing='15px' direction='column' className={cx("holding-portfolio")}>
        <Stack className={cx("holding-portfolio__header")} direction='row'>
          <TypographyElement FontWeightElement={700} FontSizeElement='16px' lineHeight='24px'>{t('copytrade.chart.holdingPortfolio.title')}</TypographyElement>
          {isRecommend && <TypographyElement ColorElement="brand" className={cx("holding-portfolio__newbuy")}>{t('copytrade.chart.holdingPortfolio.newbuy')}</TypographyElement>}
        </Stack>
        <StyledTable
          sx={{
            flex: 1,
            "& th": {
              backgroundColor: theme.palette.background.default
            }
          }}
          tableConfig={{
            ...tableConfig,
            header: {
              styles: {
                backgroundColor: theme.palette.background.default
              },
            },
          }}
          tableData={cookedData()}
          onPageChange={loadData}
          onLimitChange={loadData}
          onRowClick={() => { }}
        />
        {isMaster && <Stack direction='row' alignItems='center'>
          <TypographyElement>{t('copytrade.chart.holdingPortfolio.recommend')}</TypographyElement>
          <Switch
            checked={isRecommend}
            onChange={handleToggleRecommendModal}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: theme.palette.text.brand,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.1
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.5
              },
            }}
          />
        </Stack>}
      </Stack>
      <PopupElement
        open={isOpenRecommendModal}
        onClose={() => setIsOpenRecommendModal(false)}
        title={t('copytrade.common.infoNotification')}
        ignore={() => setIsOpenRecommendModal(false)}
        ignoreLabel={t('copytrade.common.close')}
        goNext={handleConfirmRecommend}
        content={t(isRecommend ? 'copytrade.chart.holdingPortfolio.confirmOff' : 'copytrade.chart.holdingPortfolio.confirmOn')}
        goNextLabel={t('copytrade.common.confirm')}
      />
    </>
  );
};

export default HoldingPortfolioRealtime;
